import type { ApiClient } from "../auth/apiClient";
import { WorkItemListResponseSchema } from "./types";
import type { WorkItemListResponse } from "./types";

/**
 * Lifecycle bucket a queue badge drills into. Mirrors the backend
 * `WorkItemBucket` vocabulary; absent means the unbucketed listing.
 */
export type WorkItemBucket = "open" | "pending";
export type CompletedWorkItemPageSize = 20 | 50 | 100;

export interface ListWorkItemsByQueueInput {
    readonly queueId: string;
    readonly includeCompleted: boolean;
    readonly skip?: number;
    readonly take?: number;
    /**
     * When set, narrows the listing to the rows behind a single queue
     * badge using the same classification the badge count uses. A bucket
     * implies completed items are excluded server-side regardless of
     * <c>includeCompleted</c>.
     */
    readonly bucket?: WorkItemBucket;
}

export async function fetchWorkItemsByQueue(
    client: ApiClient,
    input: ListWorkItemsByQueueInput,
): Promise<WorkItemListResponse> {
    const params = new URLSearchParams();
    params.set("includeCompleted", String(input.includeCompleted));
    if (input.skip !== undefined) params.set("skip", String(input.skip));
    if (input.take !== undefined) params.set("take", String(input.take));
    if (input.bucket !== undefined) params.set("bucket", input.bucket);

    const response = await client.fetch(
        `/queues/${encodeURIComponent(input.queueId)}/workitems?${params.toString()}`,
    );
    if (!response.ok) {
        throw new Error(
            `Failed to fetch work items for queue ${input.queueId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemListResponseSchema.parse(json);
}

export interface ListCompletedWorkItemsByQueueInput {
    readonly queueId: string;
    readonly skip: number;
    readonly take: CompletedWorkItemPageSize;
}

export async function fetchCompletedWorkItemsByQueue(
    client: ApiClient,
    input: ListCompletedWorkItemsByQueueInput,
): Promise<WorkItemListResponse> {
    const params = new URLSearchParams({
        skip: String(input.skip),
        take: String(input.take),
    });
    const response = await client.fetch(
        `/queues/${encodeURIComponent(input.queueId)}/completed-workitems?${params.toString()}`,
    );
    if (!response.ok) {
        throw new Error(
            `Failed to fetch completed work items for queue ${input.queueId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemListResponseSchema.parse(json);
}

export interface ListMyWorkItemsInput {
    readonly includeCompleted: boolean;
    readonly skip?: number;
    readonly take?: number;
}

export async function fetchMyWorkItems(
    client: ApiClient,
    input: ListMyWorkItemsInput,
): Promise<WorkItemListResponse> {
    const params = new URLSearchParams();
    params.set("includeCompleted", String(input.includeCompleted));
    if (input.skip !== undefined) params.set("skip", String(input.skip));
    if (input.take !== undefined) params.set("take", String(input.take));

    const response = await client.fetch(`/workitems/mine?${params.toString()}`);
    if (!response.ok) {
        throw new Error(
            `Failed to fetch my work items: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemListResponseSchema.parse(json);
}
